import { DependencyList, useEffect, useState } from 'react';

import { useLogging } from '../../logging';
import { ErrorType, PageView } from '../types';
import { useGetPageView } from './usePageViews';
import { useAnalytics } from '../../analytics';
import constants from '../constants';
import { deleteViewParam } from './utils';

const { VIEW_STATE_KEY, VIEW_QUERY_PARAM } = constants;

/**
 * Loads the view associated with the current view query param, if available.
 * @returns
 */
export const useLoadSavedView = (
  search?: string
): {
  loading: boolean;
  error: ErrorType;
} => {
  const { execute, pageView, loading: loadingPageView, error: pageViewError } = useGetPageView();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const urlParams = new URLSearchParams(search || window.location.search);
    const viewKey = urlParams.get(VIEW_QUERY_PARAM);
    if (!viewKey) {
      setLoading(false);
      return;
    }

    execute(viewKey);
  }, [execute, search]);

  useEffect(() => {
    if (loadingPageView) {
      return;
    }

    if (pageView) {
      window.sessionStorage.setItem(VIEW_STATE_KEY, JSON.stringify(pageView));

      const urlWithoutView = deleteViewParam();

      history.replaceState({}, '', urlWithoutView);
    } else {
      window.sessionStorage.removeItem(VIEW_STATE_KEY);
    }

    setLoading(false);
  }, [loadingPageView, pageView]);

  return {
    loading,
    error: pageViewError,
  };
};

/**
 * If the url contained a view query param, this hook will use the underlying view state to restore the view.
 * @param applyViewState The callback that handles the restoring of the view. If if returns false, the restore is not done.
 * @param deps Additional dependencies that should restore view again
 * @param onError Optional callback that will be called in case of an error during restoring the view
 */
export const useRestoreSavedView = (
  applyViewState: (viewState: any, pageView?: Omit<PageView<object>, 'data'>) => void | boolean,
  deps: DependencyList = [],
  onError?: (error: Error) => void
): void => {
  const { error } = useLogging();
  const { track } = useAnalytics();

  useEffect(() => {
    try {
      const pageView = JSON.parse(window.sessionStorage.getItem(VIEW_STATE_KEY)) as PageView<object>;
      const viewState = pageView?.data;

      if (viewState) {
        const pageViewWithouData = { ...pageView };
        delete pageViewWithouData.data;

        // check, if restore was done ("false" would indicate that we have to restore again)
        const restoreDone = applyViewState(viewState, pageViewWithouData) ?? true;
        if (restoreDone) {
          track('LoadSavedView', getTrackingObject(pageView));
          window.sessionStorage.removeItem(VIEW_STATE_KEY);
        }
      }
    } catch (e) {
      error(e, { message: `Error while restoring saved view` });
      onError?.(e);
    }

    // Commenting out the line below because
    // having a spread operator in the deps array,
    // it cannot be verified if we passed the correct dependencies

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [...deps, track, error, applyViewState]);
};

function getTrackingObject(pageView: PageView<object>) {
  if (pageView.meta && 'purpose' in pageView.meta) {
    return {
      viewKey: pageView.key,
      purpose: pageView.meta.purpose,
    };
  }

  return {
    viewKey: pageView.key,
  };
}
